import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
	EvBodyDefault,
	EvError,
	EvFlexColumn,
	EvFlexRow,
	EvHeadingLarge,
	EvIcon,
	EvLink,
	EvSection,
	EvTabs,
	FLEX_ALIGN,
	FLEX_GAP,
	SmallBackIcon
} from '@evinced-private/ui-common';

import {
	DrilldownState,
	isValidReferrer
} from 'src/apps/dashboard/components/accessibility-summary/helpers/drilldownLinkHelper';
import {
	ALL_ISSUES_TABLE_ID,
	UNIQUE_ISSUES_TABLE_ID
} from 'src/apps/dashboard/constants/Dashboard';
import { handleDashboardRedirection } from 'src/apps/dashboard/helpers/NavigationHelpers';
import { PlatformTable } from 'src/common/components/platform-table/PlatformTable';
import { TableSkeleton } from 'src/common/components/skeletons/table-skeleton/TableSkeleton';
import { isConfigurationReady, isDashboardEnabled } from 'src/common/helpers/ConfigurationHelpers';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { UNIQUE_QUERY_PARAM } from 'src/common/helpers/QueryParamsHelper';
import { RoutesHelper } from 'src/common/helpers/RoutesHelper';
import { usePlatformConfigInitializer } from 'src/common/hooks/usePlatformConfigInitializer';
import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';

import { IssuesFilters } from './components/IssuesFilters';
import { getAllIssuesColumns, getUniqueIssuesColumns } from './helpers/IssuesTableColumns';
import { useAllIssuesData } from './hooks/useAllIssuesData';
import { useFiltersWithQueryParams } from './hooks/useFiltersWithQueryParams';
import { useIssuesPageFilters } from './hooks/useIssuesPageFilters';
import { useUniqueIssuesData } from './hooks/useUniqueIssuesData';
import { ISSUES_PAGE_CONSTANTS } from './IssuesPage.constants';

import './IssuesPage.scss';

const IssuesPage: FC = () => {
	const location = useLocation();
	const history = useHistory();

	const { getTableStateById, updateTableState } = useTableState();
	const uniqueTableState = getTableStateById(UNIQUE_ISSUES_TABLE_ID);
	const allTableState = getTableStateById(ALL_ISSUES_TABLE_ID);

	usePlatformConfigInitializer();

	const { filterDefinitions, filterOptions } = useIssuesPageFilters();

	const { configuration } = useConfiguration();

	const { filters, handleFilterChange } = useFiltersWithQueryParams({
		filterDefinitions,
		filterOptions
	});
	const lastHandledSearchRef = useRef<string | null>(null);

	const resetTablesToFirstPage = useCallback(() => {
		updateTableState(ALL_ISSUES_TABLE_ID, {
			...allTableState,
			paginationInfo: {
				...allTableState.paginationInfo,
				page: 1
			}
		});

		updateTableState(UNIQUE_ISSUES_TABLE_ID, {
			...uniqueTableState,
			paginationInfo: {
				...uniqueTableState.paginationInfo,
				page: 1
			}
		});
	}, [allTableState, uniqueTableState, updateTableState]);

	const handleIssuesFilterChange = useCallback(
		(newFilters: IFilterOptions) => {
			resetTablesToFirstPage();
			handleFilterChange(newFilters);
		},
		[handleFilterChange, resetTablesToFirstPage]
	);

	useEffect(() => {
		if (lastHandledSearchRef?.current === location?.search) {
			return;
		}
		lastHandledSearchRef.current = location?.search ?? '';
		resetTablesToFirstPage();
	}, [location?.search, resetTablesToFirstPage]);

	const isUniqueFromUrl = useMemo(() => {
		const searchParams = new URLSearchParams(location.search);
		return searchParams.get(UNIQUE_QUERY_PARAM) === 'true';
	}, [location.search]);

	const activeTabIndex = isUniqueFromUrl ? 1 : 0;

	const {
		data: uniqueIssuesData,
		isFetching: isFetchingUnique,
		error: uniqueError
	} = useUniqueIssuesData(
		filters,
		uniqueTableState.paginationInfo.page,
		uniqueTableState.paginationInfo.sizePerPage,
		uniqueTableState.tableSort?.[0],
		isUniqueFromUrl
	);

	const {
		data: allIssuesData,
		isFetching: isFetchingAll,
		error: allError
	} = useAllIssuesData(
		filters,
		allTableState.paginationInfo.page,
		allTableState.paginationInfo.sizePerPage,
		allTableState.tableSort?.[0],
		!isUniqueFromUrl
	);

	const backLinkContent = (
		<EvFlexRow gap={FLEX_GAP.SMALL} align={FLEX_ALIGN.CENTER}>
			<EvIcon icon={SmallBackIcon} small />
			<span>{ISSUES_PAGE_CONSTANTS.BACK_BUTTON_TEXT}</span>
		</EvFlexRow>
	);

	const handleBackClick = (): void => {
		const { referrer } = (location.state as DrilldownState) ?? {};
		if (isValidReferrer(referrer)) {
			history.push(referrer);
		} else {
			history.push(RoutesHelper.getAccessibilityDashboardPath());
		}
	};

	const handleTabSelected = useCallback(
		(index: number) => {
			resetTablesToFirstPage();

			const searchParams = new URLSearchParams(location.search);
			if (index === 1) {
				searchParams.set(UNIQUE_QUERY_PARAM, 'true');
			} else {
				searchParams.delete(UNIQUE_QUERY_PARAM);
			}
			const newSearch = searchParams.toString();
			history.replace({
				pathname: location?.pathname,
				search: `?${newSearch ?? ''}`
			});
		},
		[history, location.pathname, location.search, resetTablesToFirstPage]
	);

	const tabs = useMemo(
		() => [
			{
				id: 'all-issues-tab',
				tablistId: 'issues-page-tabs',
				ref: null,
				isActive: activeTabIndex === 0,
				// eslint-disable-next-line @typescript-eslint/no-empty-function
				handleKeyDown: () => {},
				children: 'All issues',
				ariaControls: 'all-issues-panel',
				dataAutomationId: 'all-issues-tab'
			},
			{
				id: 'unique-issues-tab',
				tablistId: 'issues-page-tabs',
				ref: null,
				isActive: activeTabIndex === 1,
				// eslint-disable-next-line @typescript-eslint/no-empty-function
				handleKeyDown: () => {},
				children: 'Unique issues',
				ariaControls: 'unique-issues-panel',
				dataAutomationId: 'unique-issues-tab'
			}
		],
		[activeTabIndex]
	);

	const renderUniqueIssuesContent = useCallback(() => {
		const columns = getUniqueIssuesColumns();

		if (isFetchingUnique) {
			return <TableSkeleton columns={columns.length} rowCount={7} />;
		}

		if (uniqueError) {
			return <EvError error="Failed to load unique issues. Please try again." />;
		}

		return (
			<PlatformTable
				tableId={UNIQUE_ISSUES_TABLE_ID}
				columns={columns}
				tableData={uniqueIssuesData?.issues || []}
				totalCount={uniqueIssuesData?.unfilteredTotal || 0}
				totalPaginationableResults={uniqueIssuesData?.totalCount || 0}
				persistTableState
				options={{
					keyField: 'id',
					pagination: true,
					dataType: 'issues',
					title: 'Unique Issues',
					caption: 'Unique Issues Table',
					remote: true
				}}
			/>
		);
	}, [isFetchingUnique, uniqueError, uniqueIssuesData]);

	const renderAllIssuesContent = useCallback(() => {
		const columns = getAllIssuesColumns();

		if (isFetchingAll) {
			return <TableSkeleton columns={columns.length} rowCount={7} />;
		}

		if (allError) {
			return <EvError error="Failed to load all issues. Please try again." />;
		}

		return (
			<PlatformTable
				tableId={ALL_ISSUES_TABLE_ID}
				columns={columns}
				tableData={allIssuesData?.issues || []}
				totalCount={allIssuesData?.unfilteredTotal || 0}
				totalPaginationableResults={allIssuesData?.totalCount || 0}
				persistTableState
				options={{
					keyField: 'id',
					pagination: true,
					dataType: 'issues',
					title: 'All Issues',
					caption: 'All Issues Table',
					remote: true
				}}
			/>
		);
	}, [isFetchingAll, allError, allIssuesData]);

	const tabPanels = useMemo(() => {
		return [
			{
				id: 'all-issues-panel',
				ariaLabelledBy: 'issues-page-tabs-all-issues-tab',
				isActive: activeTabIndex === 0,
				children: (
					<EvSection ariaLabel="All Issues Table" className="issues-table-section">
						<div className="issues-table-container">{renderAllIssuesContent()}</div>
					</EvSection>
				)
			},
			{
				id: 'unique-issues-panel',
				ariaLabelledBy: 'issues-page-tabs-unique-issues-tab',
				isActive: activeTabIndex === 1,
				children: (
					<EvSection ariaLabel="Unique Issues Table" className="issues-table-section">
						<div className="issues-table-container">{renderUniqueIssuesContent()}</div>
					</EvSection>
				)
			}
		];
	}, [activeTabIndex, renderAllIssuesContent, renderUniqueIssuesContent]);

	if (!isConfigurationReady(configuration)) {
		return null;
	}

	if (!isDashboardEnabled(configuration)) {
		handleDashboardRedirection(history, configuration);
		return null;
	}

	return (
		<div className="issues-page">
			<EvFlexColumn gap="MEDIUM">
				<div className="issues-page__back-link">
					<EvLink
						url={RoutesHelper.getAccessibilityDashboardPath()}
						customLink={backLinkContent}
						onClick={handleBackClick}
					/>
				</div>

				<EvFlexColumn gap="SMALL">
					<EvHeadingLarge id="issues-page-title">{ISSUES_PAGE_CONSTANTS.TITLE}</EvHeadingLarge>
					<EvBodyDefault id="issues-page-description">
						{ISSUES_PAGE_CONSTANTS.DESCRIPTION}
					</EvBodyDefault>
				</EvFlexColumn>

				<IssuesFilters
					filters={filters}
					onFilterChange={handleIssuesFilterChange}
					filterDefinitions={filterDefinitions}
					filterOptions={filterOptions}
				/>

				<section className="issues-tabs-section">
					<EvTabs
						key={`issues-tabs-${activeTabIndex}`}
						id="issues-page-tabs"
						tabs={tabs}
						tabPanels={tabPanels}
						defaultTab={activeTabIndex}
						onTabSelected={handleTabSelected}
						ariaLabel="Issues tabs"
					/>
				</section>
			</EvFlexColumn>
		</div>
	);
};
export default IssuesPage;
